
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { collectionsApi } from "@/lib/api";
import toast from "react-hot-toast";
import { getSmartProductPlaceholder } from "@/lib/placeholders";

interface Collection {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  gradient: string | null;
  products_count: number;
  is_active: boolean;
  display_order: number;
}

export default function Collections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        const response = await collectionsApi.getAll();
        const collectionsData = response.collections || [];
        setCollections(collectionsData);
        console.log("Collections loaded:", collectionsData.length);
      } catch (error: any) {
        console.error("Error fetching collections:", error);
        toast.error("Failed to load collections");
        // Keep empty array to show section even on error
        setCollections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  if (loading) {
    return (
      <section id="shop-by-collection" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
              Shop by <span className="gradient-text">Collection</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Discover our curated collections designed for every occasion
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative h-64 md:h-80 rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm"
              >
                {/* Skeleton gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 opacity-20 animate-pulse" />
                
                {/* Skeleton content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8">
                  <div className="space-y-4">
                    {/* Products count skeleton */}
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    
                    {/* Title skeleton */}
                    <div className="h-8 w-32 bg-gray-300 rounded animate-pulse" />
                    
                    {/* Description skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                  
                  {/* Explore button skeleton */}
                  <div className="h-6 w-24 bg-gray-300 rounded animate-pulse" />
                </div>
                
                {/* Decorative elements skeleton */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-200 opacity-10 rounded-full -mr-16 -mt-16 animate-pulse" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gray-200 opacity-10 rounded-full -ml-12 -mb-12 animate-pulse" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show section even if no collections (for debugging)
  // if (collections.length === 0) {
  //   return null;
  // }
  return (
    <section id="shop-by-collection" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Shop by <span className="gradient-text">Collection</span>
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Discover our curated collections designed for every occasion
          </p>
        </motion.div>

        {collections.length === 0 && !loading && (
          <div className="text-center text-gray-500 mb-4">
            No collections available
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {collections.map((collection, index) => {
            // Format products count
            const countDisplay = collection.products_count > 0 
              ? `${collection.products_count}+` 
              : "0";
            
            // Default gradient if not provided
            const gradient = collection.gradient || "from-purple-500 to-pink-500";
            
            // Determine if we have an image
            const hasImage = collection.image_url && collection.image_url.trim() !== "";
            
            return (
              <Link
                key={collection.id}
                to={`/products?collection=${collection.slug}`}
                className="block"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                  className="relative h-80 md:h-96 rounded-2xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  {/* Image Background or Gradient Background */}
                  {hasImage ? (
                    <>
                      <img
                        src={collection.image_url!}
                        alt={collection.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.src = getSmartProductPlaceholder(collection.title, "collection");
                        }}
                      />
                      {/* Dark overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/90 group-hover:via-black/50 transition-all duration-300" />
                    </>
                  ) : (
                    <>
                      {/* Gradient Background when no image */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${gradient} group-hover:opacity-90 transition-opacity duration-300`}
                      />
                      {/* Pattern overlay for visual interest */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16" />
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12" />
                      </div>
                    </>
                  )}

                  {/* Content Overlay */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8 text-white">
                    {/* Products count badge */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold mb-3 w-fit"
                    >
                      {countDisplay} Products
                    </motion.div>

                    {/* Title */}
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="text-2xl md:text-3xl font-bold mb-2 drop-shadow-lg"
                    >
                      {collection.title}
                    </motion.h3>

                    {/* Description */}
                    {collection.description && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                        className="text-sm md:text-base text-white/90 mb-4 line-clamp-2 drop-shadow-md"
                      >
                        {collection.description}
                      </motion.p>
                    )}

                    {/* Explore Button */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 }}
                      whileHover={{ x: 5 }}
                      className="flex items-center space-x-2 text-base font-semibold group-hover:text-yellow-300 transition-colors"
                    >
                      <span>Explore Collection</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </motion.div>
                  </div>

                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
